import { useState } from "react";
import axios from "axios";
import { Layout } from "@/components/Layout";
import { Mail, Send, Check, Loader2, AlertCircle, Instagram, Facebook, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_EMAIL = "perasciano@gmail.com";

export default function ContattiPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const onField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail || "Errore invio.");
    }
  };

  return (
    <Layout>
      <section className="bg-[#1f2d22] text-white py-16 sm:py-20" data-testid="contatti-header">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 px-4 py-1.5 mb-5">
            <Mail className="w-3.5 h-3.5 text-[#E8C9A0]" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-[#E8C9A0]">
              Contatti
            </span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
            Scrivici <span className="italic font-light text-[#E8C9A0]">a Per Asciano.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-white/75 max-w-2xl leading-relaxed">
            Vuoi una collaborazione, una proposta o semplicemente conoscerci? Scrivici, ti risponderemo a breve.
          </p>
        </div>
      </section>

      <section className="py-14 sm:py-20 bg-[#FBF9F6]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Form */}
          <div className="lg:col-span-3">
            {status === "success" ? (
              <div data-testid="contact-success" className="rounded-3xl bg-[#2E4F3B] text-white p-10 sm:p-12 text-center">
                <div className="w-16 h-16 rounded-full bg-white/15 flex items-center justify-center mx-auto mb-5">
                  <Check className="w-8 h-8 text-[#E8C9A0]" />
                </div>
                <h2 className="font-display text-2xl sm:text-3xl font-black">Messaggio inviato.</h2>
                <p className="mt-3 text-white/80">Grazie per averci scritto, ti risponderemo a breve.</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="bg-white border-2 border-[#E5E1D8] rounded-3xl p-7 sm:p-9 space-y-5">
                <Field name="name" label="Nome e cognome *" value={form.name} onChange={onField} required testid="contact-name" />
                <Field name="email" type="email" label="Email *" value={form.email} onChange={onField} required testid="contact-email" />
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C] mb-2">Messaggio *</label>
                  <textarea
                    name="message"
                    rows={6}
                    required
                    minLength={5}
                    value={form.message}
                    onChange={onField}
                    data-testid="contact-message"
                    placeholder="Scrivi il tuo messaggio…"
                    className="w-full bg-[#FBF9F6] border-2 border-[#E5E1D8] rounded-xl px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#2E4F3B] focus:ring-2 focus:ring-[#2E4F3B]/10 transition-all resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="contact-submit"
                  className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#2E4F3B] hover:bg-[#1f3828] text-white font-bold px-8 py-4 transition-all disabled:opacity-60 shadow-lg"
                >
                  {status === "loading" ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Invio…
                    </>
                  ) : (
                    <>
                      Invia messaggio
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
                {error && (
                  <div className="flex items-start gap-3 bg-[#C15C3D]/10 border border-[#C15C3D]/30 rounded-xl p-3">
                    <AlertCircle className="w-4 h-4 text-[#C15C3D] shrink-0 mt-0.5" />
                    <p className="text-xs text-[#1A1A1A]">{error}</p>
                  </div>
                )}
              </form>
            )}
          </div>

          {/* Info */}
          <aside className="lg:col-span-2">
            <div className="bg-[#1f2d22] text-white rounded-3xl p-7 sm:p-9 sticky top-28">
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#E8C9A0] mb-4">Contatti diretti</div>
              <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-3 mb-3 hover:text-[#E8C9A0] text-white">
                <Mail className="w-5 h-5" />
                {CONTACT_EMAIL}
              </a>
              <div className="text-sm text-white/70 mb-6">
                Asciano (PI) · Toscana<br />
                C.F. 93104860502
              </div>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#E8C9A0] mb-4 pt-5 border-t border-white/15">
                Seguici
              </div>
              <div className="flex flex-col gap-3 text-base">
                <a href="https://www.instagram.com/perasciano/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/85 hover:text-white">
                  <Instagram className="w-5 h-5" /> Instagram
                </a>
                <a href="https://www.facebook.com/perasciano" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-white/85 hover:text-white">
                  <Facebook className="w-5 h-5" /> Facebook
                </a>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </Layout>
  );
}

const Field = ({ name, label, value, onChange, type = "text", required, testid }) => (
  <div>
    <label htmlFor={name} className="block text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5C5C] mb-2">
      {label}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      required={required}
      data-testid={testid}
      className="w-full bg-[#FBF9F6] border-2 border-[#E5E1D8] rounded-xl px-4 py-3 text-[#1A1A1A] focus:outline-none focus:border-[#2E4F3B] focus:ring-2 focus:ring-[#2E4F3B]/10 transition-all"
    />
  </div>
);
